import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../api/axiosClient";

export interface Client {
  client_id: string;
  business_id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  preferred_staff?: string;
  created_at: string;
  updated_at: string;
  total_appointments: number;
  total_spent: number;
  last_visit?: string;
  favorite_service?: string;
  display_order: number;
}

export interface CreateClientInput {
  business_id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  preferred_staff?: string;
}

export interface UpdateClientInput extends CreateClientInput {
  client_id: string;
}

interface ClientsState {
  clients: Client[];
  loading: boolean;
  error: string | null;
  saving: boolean;
}

const initialState: ClientsState = {
  clients: [],
  loading: false,
  error: null,
  saving: false,
};

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const res = await axiosClient.get("/clients", {
        params: { businessId },
        headers: authHeader(),
      });
      return res.data as Client[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch clients");
    }
  }
);

export const createClient = createAsyncThunk(
  "clients/createClient",
  async (input: CreateClientInput, { rejectWithValue }) => {
    try {
      const res = await axiosClient.post("/clients", input, {
        headers: authHeader(),
      });
      return res.data as { client_id: string };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create client");
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async (input: UpdateClientInput, { rejectWithValue }) => {
    try {
      await axiosClient.put(`/clients/${input.client_id}`, input, {
        headers: authHeader(),
      });
      return input;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update client");
    }
  }
);

export const reorderClients = createAsyncThunk(
  "clients/reorderClients",
  async (
    { businessId, clientIds }: { businessId: string; clientIds: string[] },
    { rejectWithValue }
  ) => {
    try {
      await axiosClient.put(
        "/clients/reorder",
        { client_ids: clientIds },
        { params: { businessId }, headers: authHeader() }
      );
      return clientIds;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to reorder clients");
    }
  }
);

export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async (clientId: string, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/clients/${clientId}`, {
        headers: authHeader(),
      });
      return clientId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete client");
    }
  }
);

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createClient.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      .addCase(updateClient.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload;
        const idx = state.clients.findIndex(
          (c) => c.client_id === updated.client_id
        );
        if (idx !== -1) {
          state.clients[idx] = { ...state.clients[idx], ...updated };
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      .addCase(reorderClients.fulfilled, (state, action) => {
        const order = action.payload;
        const byId = new Map(state.clients.map((c) => [c.client_id, c]));
        state.clients = order
          .map((id, i) => {
            const c = byId.get(id);
            return c ? { ...c, display_order: i } : undefined;
          })
          .filter((c): c is Client => !!c);
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter(
          (c) => c.client_id !== action.payload
        );
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default clientsSlice.reducer;
